import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { fetchNotifications, saveNotifications } from "actions/notificationActions";
import type { RootState } from "store";
import type { UserNotificationData } from "types";

const notificationsAdapter = createEntityAdapter<UserNotificationData>({
	selectId: (userNotification) => userNotification.userId,
});

const initialState = {
	...notificationsAdapter.getInitialState(),
	isLoading: true,
	isFetching: false,
	isSaving: false,
	originalUsers: {} as Record<string, UserNotificationData>,
	addedUserIds: [] as string[],
	updatedUserIds: [] as string[],
	deletedUserIds: [] as string[],
};

const notificationsSlice = createSlice({
	name: "notifications",
	initialState,
	reducers: {
		updateUserNotificationSettings: (state, { payload: { userId, settings } }) => {
			notificationsAdapter.updateOne(state, {
				id: userId,
				changes: { settings },
			});
			// Track as updated if it's not a new user
			if (!state.addedUserIds.includes(userId) && !state.updatedUserIds.includes(userId)) {
				state.updatedUserIds.push(userId);
			}
		},
		addUserNotification: (state, { payload }) => {
			notificationsAdapter.addOne(state, payload);
			// Track as added
			if (!state.addedUserIds.includes(payload.userId)) {
				state.addedUserIds.push(payload.userId);
			}
		},
		removeUserNotification: (state, { payload: userId }) => {
			notificationsAdapter.removeOne(state, userId);
			// Track as deleted only if it was in the original state
			if (state.originalUsers[userId]) {
				if (!state.deletedUserIds.includes(userId)) {
					state.deletedUserIds.push(userId);
				}
				// Remove from added/updated if it was there
				state.addedUserIds = state.addedUserIds.filter((id) => id !== userId);
				state.updatedUserIds = state.updatedUserIds.filter((id) => id !== userId);
			} else {
				// If it was just added, remove it from the added list
				state.addedUserIds = state.addedUserIds.filter((id) => id !== userId);
			}
		},
		resetChanges: (state) => {
			state.addedUserIds = [];
			state.updatedUserIds = [];
			state.deletedUserIds = [];
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchNotifications.pending, (state) => {
				state.isFetching = true;
			})
			.addCase(fetchNotifications.fulfilled, (state, { payload }) => {
				if (payload?.data && Array.isArray(payload.data)) {
					// Transform the API response to match the expected structure
					const transformedUsers = payload.data.map((user: any) => ({
						userId: user.RowKey,
						displayName: user.displayName,
						mail: user.mail,
						settings: {
							newsletter: user.newsletter,
							// Map from old API property names to new internal names
							releaseNote: user.releaseNote,
							recommendation: user.recommendation,
							alert: user.alert,
						},
					}));
					notificationsAdapter.setAll(state, transformedUsers);
					// Store original state for tracking changes
					state.originalUsers = transformedUsers.reduce(
						(acc, user) => {
							acc[user.userId] = user;
							return acc;
						},
						{} as Record<string, UserNotificationData>,
					);
					// Reset change tracking
					state.addedUserIds = [];
					state.updatedUserIds = [];
					state.deletedUserIds = [];
				}
				state.isFetching = false;
				state.isLoading = false;
			})
			.addCase(fetchNotifications.rejected, (state) => {
				state.isFetching = false;
				state.isLoading = false;
			})
			.addCase(saveNotifications.pending, (state) => {
				state.isSaving = true;
			})
			.addCase(saveNotifications.fulfilled, (state) => {
				state.isSaving = false;
				// Update original state after successful save
				const currentUsers = notificationsSelectors.selectAll({
					notifications: state,
				} as RootState);
				state.originalUsers = currentUsers.reduce(
					(acc, user) => {
						acc[user.userId] = user;
						return acc;
					},
					{} as Record<string, UserNotificationData>,
				);
				// Reset change tracking
				state.addedUserIds = [];
				state.updatedUserIds = [];
				state.deletedUserIds = [];
			})
			.addCase(saveNotifications.rejected, (state) => {
				state.isSaving = false;
			});
	},
});

export const {
	updateUserNotificationSettings,
	addUserNotification,
	removeUserNotification,
	resetChanges,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;

export const notificationsSelectors = notificationsAdapter.getSelectors<RootState>(
	(state) => state.notifications,
);

export const selectNotifications = (state: RootState) => state.notifications;
export const selectNotificationUsers = notificationsSelectors.selectAll;
export const selectNotificationById = notificationsSelectors.selectById;
export const selectAddedUserIds = (state: RootState) => state.notifications.addedUserIds;
export const selectUpdatedUserIds = (state: RootState) => state.notifications.updatedUserIds;
export const selectDeletedUserIds = (state: RootState) => state.notifications.deletedUserIds;
export const selectOriginalUsers = (state: RootState) => state.notifications.originalUsers;
